import Keycloak from 'keycloak-js';
import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { setKeycloakInstance } from './api/axiosConfig.js';

// Detectar errores relacionados con configuración de Keycloak
const isKeycloakConfigError = (error) => {
  if (!error) return false;
  
  const errorMessage = error?.message || '';
  const errorString = JSON.stringify(error).toLowerCase();
  
  return errorMessage.includes('CORS') ||
         errorMessage.includes('403') ||
         errorMessage.includes('Forbidden') ||
         errorMessage.includes('ERR_ABORTED') ||
         errorString.includes('403') ||
         errorString.includes('forbidden') ||
         errorString.includes('cors') ||
         // Detectar cuando Keycloak no puede inicializar correctamente
         (error === undefined || errorMessage === 'Error desconocido');
};

let keycloakInstance = null;

const getKeycloakInstance = () => {
  if (!keycloakInstance) {
    keycloakInstance = new Keycloak({
      url: import.meta.env.VITE_KEYCLOAK_URL,
      realm: import.meta.env.VITE_KEYCLOAK_REALM,
      clientId: import.meta.env.VITE_KEYCLOAK_CLIENTID,
    });
  }
  return keycloakInstance;
};

const AuthContext = createContext();

// Función para extraer roles de múltiples fuentes en el token
const extractRoles = (tokenParsed) => {
  if (!tokenParsed) {
    console.log('🔍 extractRoles: No hay tokenParsed disponible');
    return [];
  }
  
  console.log('🔍 extractRoles: Analizando token completo:', tokenParsed);
  
  const roles = new Set();
  const rolesFound = {
    realm: [],
    client: [],
    groups: [],
    custom: []
  };
  
  // 1. Roles del realm
  const realmRoles = tokenParsed.realm_access?.roles || [];
  console.log('🔍 extractRoles: Roles del realm encontrados:', realmRoles);
  realmRoles.forEach(role => {
    roles.add(role);
    rolesFound.realm.push(role);
  });
  
  // 2. Roles del cliente específico (s3-browser-frontend)
  const mainClientRoles = tokenParsed.resource_access?.['s3-browser-frontend']?.roles || [];
  console.log('🔍 extractRoles: Roles del cliente s3-browser-frontend:', mainClientRoles);
  mainClientRoles.forEach(role => {
    roles.add(role);
    rolesFound.client.push(`s3-browser-frontend.${role}`);
  });
  
  // 3. Roles de todos los clientes disponibles
  if (tokenParsed.resource_access) {
    console.log('🔍 extractRoles: resource_access completo:', tokenParsed.resource_access);
    Object.keys(tokenParsed.resource_access).forEach(clientId => {
      const clientRolesList = tokenParsed.resource_access[clientId]?.roles || [];
      console.log(`🔍 extractRoles: Roles del cliente ${clientId}:`, clientRolesList);
      clientRolesList.forEach(role => {
        // Evitar duplicados - solo procesar si no es el cliente principal ya procesado
        if (clientId !== 's3-browser-frontend') {
          roles.add(role);
        }
        rolesFound.client.push(`${clientId}.${role}`);
      });
    });
  }
  
  // 4. Roles en groups (si están configurados)
  const groups = tokenParsed.groups || [];
  console.log('🔍 extractRoles: Groups encontrados:', groups);
  groups.forEach(group => {
    let groupName = null;
    if (typeof group === 'string') {
      groupName = group;
    } else if (group.name) {
      groupName = group.name;
    } else if (group.path) {
      // Extraer nombre del path (/admin -> admin)
      groupName = group.path.replace(/^\/+/, '');
    }
    
    if (groupName) {
      roles.add(groupName);
      rolesFound.groups.push(groupName);
    }
  });
  
  // 5. Buscar claims personalizados que puedan contener roles
  const customRoleFields = ['roles', 'authorities', 'user_roles', 'permissions'];
  customRoleFields.forEach(field => {
    if (tokenParsed[field]) {
      console.log(`🔍 extractRoles: Campo personalizado ${field}:`, tokenParsed[field]);
      const customRoles = Array.isArray(tokenParsed[field]) ? tokenParsed[field] : [tokenParsed[field]];
      customRoles.forEach(role => {
        if (typeof role === 'string') {
          roles.add(role);
          rolesFound.custom.push(`${field}.${role}`);
        }
      });
    }
  });
  
  // 6. Buscar en audience y scope
  if (tokenParsed.aud) {
    console.log('🔍 extractRoles: Audience:', tokenParsed.aud);
  }
  if (tokenParsed.scope) {
    console.log('🔍 extractRoles: Scope:', tokenParsed.scope);
  }
  
  const finalRoles = Array.from(roles);
  
  console.log('🔍 extractRoles: Resumen de roles encontrados:', {
    realm: rolesFound.realm,
    client: rolesFound.client,
    groups: rolesFound.groups,
    custom: rolesFound.custom,
    total: finalRoles,
    hasAdmin: finalRoles.includes('admin')
  });
  
  // Logging específico para el rol admin
  if (finalRoles.includes('admin')) {
    console.log('✅ extractRoles: ROL ADMIN ENCONTRADO');
  } else {
    console.log('❌ extractRoles: ROL ADMIN NO ENCONTRADO');
    console.log('🔍 extractRoles: Verificar configuración de roles en Keycloak:');
    console.log('   1. Usuario tiene rol "admin" asignado?');
    console.log('   2. Rol está en realm_access o resource_access?');
    console.log('   3. Client scope incluye roles en el token?');
    console.log('   4. Hay mappers configurados para roles?');
  }
  
  return finalRoles;
};

// Función para verificar si el usuario tiene rol admin en el cliente específico
// Alineada con la lógica del backend que verifica CLIENT_ID
export const hasAdminRole = (tokenParsed, clientId = 's3-browser-frontend') => {
  if (!tokenParsed || !tokenParsed.resource_access) {
    console.log('❌ hasAdminRole: No hay tokenParsed o resource_access');
    return false;
  }
  
  const clientResource = tokenParsed.resource_access[clientId];
  if (!clientResource || !clientResource.roles) {
    console.log(`❌ hasAdminRole: No hay roles para el cliente ${clientId}`);
    return false;
  }
  
  const hasAdmin = clientResource.roles.includes('admin');
  console.log(`🔍 hasAdminRole: Cliente ${clientId}, roles:`, clientResource.roles, 'hasAdmin:', hasAdmin);
  
  return hasAdmin;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [roles, setRoles] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const initRef = useRef(false);

  useEffect(() => {
    // Evitar inicialización múltiple
    if (initRef.current) return;
    initRef.current = true;

    const keycloak = getKeycloakInstance();
    
    console.log('Inicializando Keycloak...');
    console.log('URL:', import.meta.env.VITE_KEYCLOAK_URL);
    console.log('Realm:', import.meta.env.VITE_KEYCLOAK_REALM);
    console.log('Client ID:', import.meta.env.VITE_KEYCLOAK_CLIENTID);

    // Verificar si hay errores en la URL (redirect de Keycloak con error)
    const urlParams = new URLSearchParams(window.location.search);
    const urlError = urlParams.get('error');
    if (urlError) {
      console.error('Error en URL de Keycloak:', {
        error: urlError,
        description: urlParams.get('error_description'),
        state: urlParams.get('state')
      });
      setError({ message: `Error de Keycloak: ${urlError}` });
      setIsLoading(false);
      return;
    }

    keycloak.init({ 
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      checkLoginIframe: true,
      promiseType: 'native',
      flow: 'implicit',
      enableLogging: true
    }).then(authenticated => {
      console.log('Keycloak inicializado. Autenticado:', authenticated);
      setIsLoading(false);
      
      // Configurar la instancia de Keycloak en axiosConfig para renovación automática de tokens
      setKeycloakInstance(keycloak);
      
      if (authenticated) {
        setToken(keycloak.token);
        const extractedRoles = extractRoles(keycloak.tokenParsed);
        setRoles(extractedRoles);
        setIsAdmin(hasAdminRole(keycloak.tokenParsed));
        console.log('Usuario autenticado:', keycloak.tokenParsed?.preferred_username);
        console.log('Roles extraídos:', extractedRoles);
        console.log('Es admin:', hasAdminRole(keycloak.tokenParsed));
        
        // Renovar token automáticamente cada 5 minutos (pero solo si está próximo a expirar)
        const interval = setInterval(() => {
          // Solo renovar si el token expira en los próximos 120 segundos
          keycloak.updateToken(120).then(refreshed => {
            if (refreshed) {
              console.log('🔄 Token renovado automáticamente por intervalo');
              setToken(keycloak.token);
              const extractedRoles = extractRoles(keycloak.tokenParsed);
              setRoles(extractedRoles);
              setIsAdmin(hasAdminRole(keycloak.tokenParsed));
            } else {
              console.log('ℹ️ Token aún válido en verificación automática');
            }
          }).catch(err => {
            const errorMsg = err?.message || err?.toString() || 'Error desconocido';
            console.error('❌ Error en renovación automática:', errorMsg);
            
            // Si hay error de sesión, limpiar el intervalo
            if (errorMsg.includes('logout') || 
                errorMsg.includes('session') || 
                errorMsg.includes('invalid_grant') ||
                errorMsg.includes('Token expired')) {
              clearInterval(interval);
              console.log('🔑 Sesión expirada, limpiando intervalo de renovación');
              // No forzar logout aquí, dejar que lo maneje el error 401 en requests
            }
          });
        }, 300000); // 5 minutos

        // Cleanup en unmount
        return () => clearInterval(interval);
      } else {
        console.log('Usuario no autenticado - redirigiendo a login');
        keycloak.login();
      }
    }).catch(error => {
      console.error('Error inicializando Keycloak:', error);
      setError(error || { message: 'Error de conexión con Keycloak' });
      setIsLoading(false);
    });
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Conectando con Keycloak...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 max-w-lg">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Problema de Configuración</h2>
          <p className="text-gray-600 mb-4">
            El cliente <code className="bg-gray-100 px-2 py-1 rounded">s3-browser-frontend</code> no está configurado correctamente en Keycloak.
          </p>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-left">
            <h3 className="font-semibold text-yellow-800 mb-2">Posibles causas:</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Cliente no existe en el realm <strong>master</strong></li>
              <li>• URL <code>http://localhost:3000</code> no está en redirects válidos</li>
              <li>• Cliente deshabilitado o mal configurado</li>
              <li>• Problemas de CORS en el servidor Keycloak</li>
            </ul>
          </div>

          <p className="text-sm text-gray-500 mb-4">
            Error: {error.error || error.message || 'Error desconocido'}
          </p>
          
          <div className="space-y-3">
            <button 
              onClick={() => window.location.reload()} 
              className="block w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
               Reintentar
            </button>
            
            <details className="text-left">
              <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                📋 Ver instrucciones de configuración
              </summary>
              <div className="mt-2 p-3 bg-gray-50 rounded text-xs text-gray-700">
                <p className="font-semibold mb-2">Para configurar el cliente en Keycloak:</p>
                <ol className="space-y-1 list-decimal list-inside">
                  <li>Accede al admin console de Keycloak</li>
                  <li>Ve al realm <strong>master</strong></li>
                  <li>Crea cliente <strong>s3-browser-frontend</strong></li>
                  <li>Agrega <code>http://localhost:3000/*</code> a Valid redirect URIs</li>
                  <li>Habilita "Standard flow" y "Implicit flow"</li>
                </ol>
              </div>
            </details>
          </div>
        </div>
      </div>
    );
  }

  const keycloak = getKeycloakInstance();
  const isAuthenticated = !!token && !!keycloak.authenticated;

  return (
    <AuthContext.Provider value={{ 
      user: keycloak.tokenParsed, 
      token, 
      keycloak, 
      roles, 
      isAdmin, 
      isLoading,
      isAuthenticated,
      hasAdminRole: (clientId) => hasAdminRole(keycloak.tokenParsed, clientId) 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Exportar función extractRoles para debugging
export { extractRoles };
